import { useState, useEffect } from "react";
import { Check, Clock, UserCheck, UserX, Copy } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_PIN = "1010";

export default function ApplicationsView() {
  const [apps, setApps] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);
  const [filter, setFilter] = useState("pending");

  useEffect(() => { loadApps(); }, []);

  async function loadApps() {
    try {
      const res = await axios.get(`${API}/applications`);
      setApps(res.data || []);
    } catch { /* */ }
    finally { setLoaded(true); }
  }

  async function approveApp(appId) {
    try {
      const res = await axios.post(`${API}/applications/${appId}/approve`, { pin: ADMIN_PIN });
      if (res.data.success) await loadApps();
    } catch { /* */ }
  }

  async function rejectApp(appId) {
    try {
      await axios.post(`${API}/applications/${appId}/reject`, { pin: ADMIN_PIN });
      await loadApps();
    } catch { /* */ }
  }

  function getInviteUrl(token) {
    return `${window.location.origin}/invite/${token}`;
  }

  function copyInviteLink(token) {
    const url = getInviteUrl(token);
    navigator.clipboard.writeText(url).then(() => {
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    }).catch(() => {
      window.prompt("Copy this invite link:", url);
    });
  }

  const filtered = apps.filter(a => {
    if (filter === "all") return true;
    return a.status === filter;
  }).sort((a, b) => (b.submittedAt || "").localeCompare(a.submittedAt || ""));

  const pendingCount = apps.filter(a => a.status === "pending").length;
  const approvedCount = apps.filter(a => a.status === "approved").length;
  const completedCount = apps.filter(a => a.status === "completed").length;

  return (
    <div data-testid="applications-view">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-base font-bold text-zinc-900">Member Applications</h3>
        <div className="flex gap-1">
          {[
            { key: "pending", label: `Pending (${pendingCount})` },
            { key: "approved", label: `Approved (${approvedCount})` },
            { key: "completed", label: `Joined (${completedCount})` },
            { key: "all", label: "All" },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1 text-[11px] font-bold rounded-md transition-colors ${
                filter === f.key ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {!loaded ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-3 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-zinc-400 italic py-8 text-center">No {filter === "all" ? "" : filter} applications yet.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map(app => (
            <div key={app.appId || app.name} className={`border rounded-xl overflow-hidden ${
              app.status === "pending" ? "border-amber-200 bg-amber-50/50" :
              app.status === "approved" ? "border-blue-200 bg-blue-50/50" :
              app.status === "completed" ? "border-green-200 bg-green-50/50" :
              "border-red-200 bg-red-50/50"
            }`}>
              <div className="px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-bold text-zinc-900">{app.name}</span>
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${
                        app.status === "pending" ? "bg-amber-100 text-amber-700" :
                        app.status === "approved" ? "bg-blue-100 text-blue-700" :
                        app.status === "completed" ? "bg-green-100 text-green-700" :
                        "bg-red-100 text-red-700"
                      }`}>
                        {app.status}
                      </span>
                    </div>
                    {app.business && <p className="text-xs font-semibold text-zinc-600">{app.business}{app.industry ? ` · ${app.industry}` : ""}</p>}
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
                      {app.email && <span className="text-[11px] text-zinc-500">{app.email}</span>}
                      {app.phone && <span className="text-[11px] text-zinc-500">{app.phone}</span>}
                    </div>
                    {app.heardAbout && <p className="text-[11px] text-zinc-400 mt-1">Referred by: <span className="font-semibold text-zinc-600">{app.heardAbout}</span></p>}
                    {app.message && <p className="text-[11px] text-zinc-500 mt-1 italic">"{app.message}"</p>}
                    <p className="text-[10px] text-zinc-400 mt-1.5">
                      <Clock className="w-3 h-3 inline mr-0.5" />
                      Applied {app.submittedAt ? new Date(app.submittedAt).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : ""}
                    </p>
                  </div>
                </div>

                {app.status === "pending" && (
                  <div className="flex gap-2 mt-3 pt-3 border-t border-amber-200">
                    <button onClick={() => approveApp(app.appId)} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors">
                      <UserCheck className="w-3.5 h-3.5" /> Approve
                    </button>
                    <button onClick={() => rejectApp(app.appId)} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors">
                      <UserX className="w-3.5 h-3.5" /> Reject
                    </button>
                  </div>
                )}

                {app.status === "approved" && app.inviteToken && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <p className="text-[10px] font-bold text-blue-700 uppercase tracking-widest mb-1.5">Invite Link — send to applicant</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-white border border-blue-200 rounded-lg px-3 py-2 text-[11px] font-mono text-blue-800 truncate">
                        {getInviteUrl(app.inviteToken)}
                      </div>
                      <button
                        onClick={() => copyInviteLink(app.inviteToken)}
                        className={`inline-flex items-center gap-1 px-3 py-2 text-xs font-bold rounded-lg transition-colors flex-shrink-0 ${
                          copiedToken === app.inviteToken ? "bg-green-100 text-green-700" : "bg-blue-600 text-white hover:bg-blue-700"
                        }`}
                      >
                        {copiedToken === app.inviteToken ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                      </button>
                    </div>
                  </div>
                )}

                {app.status === "completed" && (
                  <div className="mt-3 pt-3 border-t border-green-200">
                    <p className="text-xs text-green-700 font-semibold">
                      <Check className="w-3.5 h-3.5 inline mr-1" />
                      Profile completed — now on public directory and tracker
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
